<script lang="ts" setup>
	import { ref, defineProps, defineEmits, watch, nextTick, computed } from 'vue'
	import Popper from 'vue3-popper'
	import { VueFinalModal } from 'vue-final-modal'
	import { TriangleAlert } from 'lucide-vue-next'
	import { useI18n } from 'vue-i18n'

	const _sharedTranslations = {
		messages: {
			en: {
				buttons: {
					confirm: 'Confirm',
					cancel: 'Cancel',
				},
			},
			de: {
				buttons: {
					confirm: 'Bestätigen',
					cancel: 'Abbrechen',
				},
			},
		},
	}
	// if the popper version is used:
	// try to min-h-96 on the parent element if using overflow-auto

	interface Props {
		modal?: boolean
		title?: string
		text?: string
		confirmOnEnter?: boolean | string
	}

	const props = withDefaults(defineProps<Props>(), {
		modal: false,
		title: '',
		text: '',
		confirmOnEnter: false,
	})

	const emits = defineEmits(['confirm'])
	const { t } = useI18n(_sharedTranslations)

	const isDialogOpen = ref(false)
	const cancelButtonRef = ref<HTMLButtonElement | null>(null)
	const confirmButtonRef = ref<HTMLButtonElement | null>(null)

	function focusActionButton() {
		if (props.confirmOnEnter) confirmButtonRef.value?.focus()
		// else cancelButtonRef.value?.focus()
	}

	function openDialog() {
		isDialogOpen.value = true
		focusActionButton()
	}
	function cancelDialog() {
		isDialogOpen.value = false
	}
	function confirmDialog() {
		isDialogOpen.value = false
		emits('confirm')
	}

	function confirmPopper(close: () => void) {
		close()
		emits('confirm')
	}
</script>

<template>
	<template v-if="modal">
		<span @click="openDialog">
			<slot />
		</span>

		<vue-final-modal
			v-model="isDialogOpen"
			:click-to-close="false"
			:esc-to-close="false"
			:modal-id="'confirm-dialog'"
			class="vfm-modal"
			@opened="focusActionButton">
			<div class="vfm-modal-box _small">
				<h3 v-if="title" class="flex items-center gap-2 p-1 text-lg font-bold">
					<TriangleAlert class="text-warning inline h-6 w-6" />
					{{ title }}
				</h3>
				<p v-if="text" class="p-1 whitespace-break-spaces">{{ text }}</p>
				<div class="modal-action">
					<button ref="cancelButtonRef" class="btn" @click="cancelDialog">
						{{ t('buttons.cancel') }}
					</button>
					<button ref="confirmButtonRef" class="btn btn-primary" @click="confirmDialog">
						{{ t('buttons.confirm') }}
					</button>
				</div>
			</div>
		</vue-final-modal>
	</template>

	<!-- Popper Version -->
	<template v-else>
		<Popper arrow placement="top">
			<span>
				<slot />
			</span>
			<template #content="{ close }">
				<div class="text-left">
					<h3 v-if="title" class="mb-2 text-lg font-bold">{{ title }}</h3>
					<p v-if="text" class="mb-4">{{ text }}</p>
					<div class="flex justify-end gap-2">
						<button class="btn btn-sm btn-primary w-36" @click.stop="confirmPopper(close)">
							{{ t('buttons.confirm') }}
						</button>
					</div>
				</div>
			</template>
		</Popper>
	</template>
</template>
